type SupportedLanguage = 'en' | 'de' | 'vi' | 'pl';

const SUPPORTED_LANGUAGES: SupportedLanguage[] = ['en', 'de', 'vi', 'pl'];
const DEFAULT_LANGUAGE: SupportedLanguage = 'de';
const LANGUAGE_KEY = 'language';
const USER_SET_FLAG_KEY = 'language-user-set';

/**
 * Detects the browser's preferred language and maps it to a supported language
 */
function getBrowserLanguage(): SupportedLanguage | null {
  const browserLang = navigator.language.toLowerCase();

  // Direct matches (e.g., 'en', 'de', 'vi', 'pl')
  if (SUPPORTED_LANGUAGES.includes(browserLang as SupportedLanguage)) {
    return browserLang as SupportedLanguage;
  }

  // Language code matches (e.g., 'en-US' -> 'en', 'de-DE' -> 'de')
  const langCode = browserLang.split('-')[0] as SupportedLanguage;
  if (SUPPORTED_LANGUAGES.includes(langCode)) {
    return langCode;
  }

  return null;
}

/**
 * Gets the initial language based on priority:
 * 1. User's explicit choice (if 'language-user-set' flag exists)
 * 2. Company/branch default language
 * 3. Browser locale detection
 * 4. Fallback to default ('de')
 */
export function getInitialLanguage(companyDefaultLanguage?: string): SupportedLanguage {
  // Priority 1: User's explicit choice
  const userSetFlag = localStorage.getItem(USER_SET_FLAG_KEY);
  const storedLanguage = localStorage.getItem(LANGUAGE_KEY);

  if (userSetFlag === 'true' && storedLanguage && SUPPORTED_LANGUAGES.includes(storedLanguage as SupportedLanguage)) {
    return storedLanguage as SupportedLanguage;
  }

  // Priority 2: Company default language
  if (companyDefaultLanguage && SUPPORTED_LANGUAGES.includes(companyDefaultLanguage as SupportedLanguage)) {
    return companyDefaultLanguage as SupportedLanguage;
  }

  // Priority 3: Browser locale
  const browserLanguage = getBrowserLanguage();
  if (browserLanguage) {
    return browserLanguage;
  }

  // Priority 4: Fallback
  return DEFAULT_LANGUAGE;
}

/**
 * Sets the language and marks it as user-selected
 * This should be called when the user explicitly selects a language
 */
export function setUserLanguage(
  language: SupportedLanguage,
  changeLanguageFn: (lang: string) => void | Promise<void>
): void {
  localStorage.setItem(LANGUAGE_KEY, language);
  localStorage.setItem(USER_SET_FLAG_KEY, 'true');
  const result = changeLanguageFn(language);
  // Handle promise if returned
  if (result instanceof Promise) {
    result.catch((error) => {
      console.error('Failed to change language:', error);
    });
  }
}

/**
 * Sets the language automatically (not user-selected)
 * This should be called for auto-detected languages (company default, browser locale)
 */
export function setAutoLanguage(
  language: SupportedLanguage,
  changeLanguageFn: (lang: string) => void | Promise<void>
): void {
  const userSetFlag = localStorage.getItem(USER_SET_FLAG_KEY);

  // Only set auto language if user hasn't explicitly chosen one
  if (userSetFlag !== 'true') {
    localStorage.setItem(LANGUAGE_KEY, language);
    const result = changeLanguageFn(language);
    // Handle promise if returned
    if (result instanceof Promise) {
      result.catch((error) => {
        console.error('Failed to change language:', error);
      });
    }
  }
}

/**
 * Clears the user-set flag (useful for testing or reset)
 */
export function clearUserLanguagePreference(): void {
  localStorage.removeItem(USER_SET_FLAG_KEY);
}
